import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { Input } from '#pplx/frontend/aether/components/Input/index.js';
import { useMenu } from '#pplx/frontend/aether/components/useMenu/index.js';
import { useScrollArea } from '#pplx/frontend/aether/components/useScrollArea/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { SkillMenuItem } from '#pplx/frontend/apps/ask/ask-input/ask-input-plus-menu/components/SkillMenuItem.js';
import { PLUS_MENU_ITEM_SELECTED_EVENT_NAME, plusMenuItemSelectedEvent, } from '#pplx/frontend/apps/ask/events/ask-input-plus-menu/analytics.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import classNames from 'classnames';
import { memo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
// Must be rendered as a child of DropdownMenu.Submenu so useMenu/useScrollArea resolve
// against the submenu's MenuProvider — calling them in the parent component
// would read the outer menu context and ArrowDown would move focus outside the
// submenu, triggering Radix's onFocusOutside and closing it.
export const SkillsMenuContent = memo(function SkillsMenuContent({ searchQuery, setSearchQuery, filteredSkills, isLoading, onSkillSelect, showSkillDescriptionPopover, }) {
    const { $t } = useIntl();
    const { track } = useInstrumentation();
    const { isMobileStyle } = useAetherContext();
    const { canScrollUp } = useScrollArea();
    const menu = useMenu();
    const handleKeyboardSkillSelect = useCallback((skill) => {
        track(plusMenuItemSelectedEvent({
            name: PLUS_MENU_ITEM_SELECTED_EVENT_NAME,
            data: {
                item: 'skill',
                value: skill.id,
            },
        }));
        onSkillSelect(skill);
    }, [onSkillSelect, track]);
    const onKeyDown = useCallback((event) => {
        if (event.key === 'ArrowDown') {
            // WHY preventDefault: suppress default cursor-to-end behavior so focus moves to the first menu item
            event.preventDefault();
            menu?.focusFirstMenuItem();
            return;
        }
        // WHY stopPropagation: prevent Radix from interpreting keystrokes as menu item navigation while typing in the search input
        event.stopPropagation();
        if (event.key === 'Enter') {
            // WHY preventDefault: suppress Radix's default Enter handling (which would close the menu) and instead select the first skill
            event.preventDefault();
            const firstSkill = filteredSkills.at(0);
            if (firstSkill) {
                handleKeyboardSkillSelect(firstSkill);
            }
        }
    }, [menu, filteredSkills, handleKeyboardSkillSelect]);
    return (_jsxs(_Fragment, { children: [_jsx(BoxPrimitive, { bg: isMobileStyle ? 'background' : 'raised', className: classNames('py-1 md:px-1 md:sticky top-0 z-10', 'transition-shadow duration-normal', canScrollUp
                    ? 'md:shadow-[0_6px_6px_-2px_rgb(from_var(--bg-raised)_r_g_b_/_0.9)]'
                    : 'md:shadow-none'), onKeyDown: onKeyDown, children: _jsx(Input, { type: "search", size: "small", "aria-label": $t({
                        defaultMessage: 'Search skills',
                        description: 'Accessible label for the search input in the skills menu',
                        id: '58911ee4d9',
                    }), placeholder: $t({
                        defaultMessage: 'Search skills',
                        description: 'Placeholder text for the search input in the skills menu',
                        id: 'e1987ea85c',
                    }), value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), onClear: () => setSearchQuery('') }) }), filteredSkills.map((skill) => (_jsx(SkillMenuItem, { skill: skill, onSkillClick: onSkillSelect, showDescriptionPopover: showSkillDescriptionPopover }, skill.id))), filteredSkills.length === 0 && !isLoading && (_jsx(DropdownMenu.InfoItem, { children: _jsx(FormattedMessage, { defaultMessage: "No skills found", description: "Empty state message in the skills menu when search returns no results", id: "0aa37c9c32" }) }))] }));
});
//# sourceMappingURL=SkillsMenuContent.js.map